import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import api from '../../lib/api';
import TagsProductFilter from './TagsProductFilter';

const AGE_OPTIONS = [3, 6, 8, 10, 14];
const DUREE_OPTIONS = [
  { value: 30, label: '< 30 min' },
  { value: 60, label: '30-60 min' },
  { value: 120, label: '> 60 min' },
];

/**
 * SearchFiltersPanel V3 — Facets UX améliorée (Phase 3.4.4)
 * - Compteurs (N) à côté de chaque option
 * - Compteur total dans le header de section
 * - Filtres à 0 résultat désactivés visuellement
 */
export default function SearchFiltersPanel({ filters, setFilter, clearAllFilters, contextFilters = {}, apiEndpoint = '/products' }) {
  const [publics, setPublics] = useState([]);
  const [types, setTypes] = useState([]);
  const [distributeurs, setDistributeurs] = useState([]);
  const [editeurs, setEditeurs] = useState([]);
  const [facets, setFacets] = useState(null);
  const [sections, setSections] = useState({ age: true, joueurs: true, duree: true, pvc: true, public: true, type: true, tags: false, distrib: false, editeur: false });
  const facetsAbortRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [pubRes, typeRes, partRes] = await Promise.all([
          api.get('/referentiels/publics_cibles?include_inactive=false'),
          api.get('/referentiels/types_jeu?include_inactive=false'),
          api.get('/partners?is_active=true&limit=200'),
        ]);
        setPublics((pubRes.data?.items || []).map(i => i.value || i.label));
        setTypes((typeRes.data?.items || []).map(i => i.value || i.label));
        const partners = Array.isArray(partRes.data) ? partRes.data : (partRes.data?.partners || partRes.data?.items || []);
        setDistributeurs(partners.filter(p => (p.capabilities || []).includes('distributor')).map(p => ({ id: p.id, nom: p.nom })));
        setEditeurs(partners.filter(p => (p.capabilities || []).includes('publisher')).map(p => ({ id: p.id, nom: p.nom })));
      } catch (e) { console.error('Filters load error:', e); }
    };
    load();
  }, []);

  // Stabilize for useCallback deps
  const contextKey = useMemo(() => JSON.stringify(contextFilters), [contextFilters]);

  const fetchFacets = useCallback(async () => {
    if (facetsAbortRef.current) facetsAbortRef.current.abort();
    const controller = new AbortController();
    facetsAbortRef.current = controller;
    try {
      const params = new URLSearchParams();
      const ctx = JSON.parse(contextKey);
      Object.entries(ctx).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (apiEndpoint && apiEndpoint.startsWith('/listings/')) {
        params.set('listing_type', apiEndpoint.replace('/listings/', ''));
      }
      const f = filters;
      if (f.age_lte) params.set('age_lte', f.age_lte);
      if (f.nb_joueurs) params.set('nb_joueurs', f.nb_joueurs);
      if (f.temps_max) params.set('temps_max', f.temps_max);
      if (f.pvc_min) params.set('pvc_min', f.pvc_min);
      if (f.pvc_max) params.set('pvc_max', f.pvc_max);
      if (f.public_cible?.length > 0) params.set('public_cible', f.public_cible.join(','));
      if (f.type_jeu?.length > 0) params.set('type_jeu', f.type_jeu[0]);
      if (f.tags_produit_ids?.length > 0) {
        params.set('tags_produit_ids', f.tags_produit_ids.join(','));
        params.set('tags_produit_mode', 'and');
      }
      if (f.partner_id) params.set('partner_id', f.partner_id);
      if (f.editeur) params.set('editeur', f.editeur);

      const res = await api.get(`/products/facets?${params.toString()}`, { signal: controller.signal });
      setFacets(res.data);
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        console.error('[Facets] error:', err);
      }
    }
  }, [filters, contextKey, apiEndpoint]);

  useEffect(() => { fetchFacets(); }, [fetchFacets]);

  const toggle = (key) => setSections(s => ({ ...s, [key]: !s[key] }));
  const hasFilters = Object.keys(filters).length > 0;

  // Helpers
  const getCount = (facetKey, value) => facets?.[facetKey]?.[value] ?? null;
  const getSectionTotal = (facetKey, visibleValues) => {
    if (!facets?.[facetKey] || !visibleValues) return null;
    return visibleValues.reduce((sum, val) => sum + (facets[facetKey][val] || 0), 0);
  };

  const SectionHeader = ({ id, label, total }) => (
    <button onClick={() => toggle(id)} className="flex items-center justify-between w-full py-2 text-sm font-semibold text-gray-800 hover:text-gray-900">
      <span>
        {label}
        {total !== null && total !== undefined && (
          <span className="ml-1 text-xs font-normal text-gray-400">({total})</span>
        )}
      </span>
      {sections[id] ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
    </button>
  );

  const CheckboxItem = ({ label, checked, onChange, count, testId }) => {
    const isZero = count === 0;
    return (
      <label
        className={`flex items-center gap-2 py-0.5 px-1 text-xs rounded cursor-pointer transition-colors ${
          isZero ? 'text-gray-300 cursor-default' : 'text-gray-700 hover:bg-gray-50'
        }`}
        data-testid={testId}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={isZero ? undefined : onChange}
          disabled={isZero}
          className="h-3.5 w-3.5 rounded border-gray-300 text-gray-800 focus:ring-gray-400 disabled:opacity-40"
        />
        <span className={isZero ? 'line-through' : ''}>{label}</span>
        {count !== null && count !== undefined && (
          <span className={`text-[10px] ml-auto tabular-nums ${isZero ? 'text-gray-300' : 'text-gray-400'}`}>
            ({count})
          </span>
        )}
      </label>
    );
  };

  return (
    <div className="w-full" data-testid="search-filters-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Filtres</h3>
        {hasFilters && (
          <button onClick={clearAllFilters} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700" data-testid="reset-all-filters">
            <RotateCcw className="h-3 w-3" /> Réinitialiser
          </button>
        )}
      </div>

      <div className="space-y-1 divide-y divide-gray-100">
        {/* ÂGE */}
        <div>
          <SectionHeader id="age" label="Âge" />
          {sections.age && (
            <div className="pb-3 flex flex-wrap gap-1.5">
              {AGE_OPTIONS.map(age => (
                <button
                  key={age}
                  onClick={() => setFilter('age_lte', filters.age_lte === age ? undefined : age)}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                    filters.age_lte === age
                      ? 'bg-gray-800 text-white border-gray-800'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                  data-testid={`filter-age-${age}`}
                >
                  {age}+ ans
                </button>
              ))}
            </div>
          )}
        </div>

        {/* JOUEURS */}
        <div>
          <SectionHeader id="joueurs" label="Joueurs" />
          {sections.joueurs && (
            <div className="pb-3">
              <input
                type="number" min={1} max={20}
                value={filters.nb_joueurs || ''}
                onChange={e => setFilter('nb_joueurs', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="Nb joueurs"
                className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                data-testid="filter-nb-joueurs"
              />
            </div>
          )}
        </div>

        {/* DURÉE */}
        <div>
          <SectionHeader id="duree" label="Durée" />
          {sections.duree && (
            <div className="pb-3 flex flex-wrap gap-1.5">
              {DUREE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFilter('temps_max', filters.temps_max === opt.value ? undefined : opt.value)}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                    filters.temps_max === opt.value
                      ? 'bg-gray-800 text-white border-gray-800'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                  data-testid={`filter-duree-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* PVC */}
        <div>
          <SectionHeader id="pvc" label="Prix (PVC)" />
          {sections.pvc && (
            <div className="pb-3 flex gap-2">
              <input type="number" min={0} step={1} value={filters.pvc_min || ''}
                onChange={e => setFilter('pvc_min', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="Min €"
                className="w-1/2 px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                data-testid="filter-pvc-min" />
              <input type="number" min={0} step={1} value={filters.pvc_max || ''}
                onChange={e => setFilter('pvc_max', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="Max €"
                className="w-1/2 px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                data-testid="filter-pvc-max" />
            </div>
          )}
        </div>

        {/* PUBLIC CIBLE */}
        <div>
          <SectionHeader id="public" label="Public cible" total={getSectionTotal('public_cible', publics)} />
          {sections.public && (
            <div className="pb-3 space-y-0.5">
              {publics.map(p => (
                <CheckboxItem
                  key={p}
                  label={p}
                  checked={(filters.public_cible || []).includes(p)}
                  onChange={() => {
                    const cur = filters.public_cible || [];
                    setFilter('public_cible', cur.includes(p) ? cur.filter(x => x !== p) : [...cur, p]);
                  }}
                  count={getCount('public_cible', p)}
                  testId={`filter-public-${p}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* TYPE DE JEU */}
        <div>
          <SectionHeader id="type" label="Type de jeu" total={getSectionTotal('type_jeu', types)} />
          {sections.type && (
            <div className="pb-3 space-y-0.5 max-h-48 overflow-y-auto">
              {types.map(t => (
                <CheckboxItem
                  key={t}
                  label={t}
                  checked={(filters.type_jeu || []).includes(t)}
                  onChange={() => {
                    const cur = filters.type_jeu || [];
                    setFilter('type_jeu', cur.includes(t) ? cur.filter(x => x !== t) : [...cur, t]);
                  }}
                  count={getCount('type_jeu', t)}
                  testId={`filter-type-${t}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* TAGS PRODUIT */}
        <div>
          <SectionHeader id="tags" label="Tags Produit" />
          {sections.tags && (
            <div className="pb-3">
              <TagsProductFilter
                selected={filters.tags_produit_ids || []}
                onChange={(ids) => setFilter('tags_produit_ids', ids)}
                facets={facets?.tags_produit_resolved || {}}
              />
            </div>
          )}
        </div>

        {/* DISTRIBUTEUR */}
        <div>
          <SectionHeader id="distrib" label="Distributeur" />
          {sections.distrib && (
            <div className="pb-3">
              <select value={filters.partner_id || ''}
                onChange={e => setFilter('partner_id', e.target.value || undefined)}
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                data-testid="filter-distributeur">
                <option value="">Tous</option>
                {distributeurs.map(d => (<option key={d.id} value={d.id}>{d.nom}</option>))}
              </select>
            </div>
          )}
        </div>

        {/* ÉDITEUR */}
        <div>
          <SectionHeader id="editeur" label="Éditeur" />
          {sections.editeur && (
            <div className="pb-3">
              <select value={filters.editeur || ''}
                onChange={e => setFilter('editeur', e.target.value || undefined)}
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400"
                data-testid="filter-editeur">
                <option value="">Tous</option>
                {editeurs.map(ed => (<option key={ed.id} value={ed.nom}>{ed.nom}</option>))}
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
